// src/features/incidents/IncidentsIoTDashboard.jsx
import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Stack, Button, Switch, FormControlLabel, Alert,
  LinearProgress, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem
} from '@mui/material';
import {
  PlayArrow, Stop, Settings, Warning, Error, CheckCircle,
  TrendingUp, DeviceThermostat, Vibration, Speed, Opacity, FlashOn
} from '@mui/icons-material';
import SensorsIcon from '@mui/icons-material/Sensors';
import { useI18n } from '../../i18n/i18n';
import incidentsIoTMonitor from './incidentsIoT';


export default function IncidentsIoTDashboard() {
  const { t } = useI18n();
  const [sensorData, setSensorData] = useState({});
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [thresholds, setThresholds] = useState({ ...incidentsIoTMonitor.alertThresholds });
  const [connectionStatus, setConnectionStatus] = useState('disconnected');

  useEffect(() => {
    // Обробник оновлень даних датчиків
    const handleSensorUpdate = (event) => {
      const { machineId, sensors } = event.detail;
      setSensorData(prev => ({
        ...prev,
        [machineId]: sensors
      }));
    };

    window.addEventListener('iotSensorUpdate', handleSensorUpdate);

    // Завантаження початкових даних
    setSensorData(incidentsIoTMonitor.getAllSensorData());
    setIsMonitoring(incidentsIoTMonitor.connected);
    setConnectionStatus(incidentsIoTMonitor.connected ? 'connected' : 'disconnected');

    return () => {
      window.removeEventListener('iotSensorUpdate', handleSensorUpdate);
    };
  }, []);

  const handleStartMonitoring = async () => {
    try {
      setConnectionStatus('connecting');
      await incidentsIoTMonitor.connectToPlatform();
      setIsMonitoring(true);
      setConnectionStatus('connected');
    } catch (error) {
      console.error('Помилка запуску моніторингу:', error);
      setConnectionStatus('error');
    }
  };

  const handleStopMonitoring = () => {
    incidentsIoTMonitor.stopMonitoring();
    setIsMonitoring(false);
    setConnectionStatus('disconnected');
  };

  const handleUpdateThresholds = () => {
    incidentsIoTMonitor.updateThresholds(thresholds);
    setSettingsOpen(false);
  };

  const getStatusColor = (status) => {
    const colors = {
      normal: 'success',
      warning: 'warning',
      critical: 'error',
      disconnected: 'default',
      connecting: 'warning',
      connected: 'success',
      error: 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      normal: <CheckCircle fontSize="small" />,
      warning: <Warning fontSize="small" />,
      critical: <Error fontSize="small" />,
      disconnected: <Error fontSize="small" />,
      connecting: <TrendingUp fontSize="small" />,
      connected: <CheckCircle fontSize="small" />,
      error: <Error fontSize="small" />
    };
    return icons[status] || <CheckCircle fontSize="small" />;
  };

  const getSensorIcon = (sensorType) => {
    const icons = {
      temperature: <DeviceThermostat fontSize="small" />,
      vibration: <Vibration fontSize="small" />,
      pressure: <Speed fontSize="small" />,
      humidity: <Opacity fontSize="small" />,
      power_consumption: <FlashOn fontSize="small" />
    };
    return icons[sensorType] || <DeviceThermostat fontSize="small" />;
  };

  const machines = Object.keys(sensorData);
  const allSensors = machines.flatMap(machineId => 
    Object.entries(sensorData[machineId] || {}).map(([sensorType, data]) => ({
      machineId,
      sensorType,
      ...data
    }))
  );

  const criticalSensors = allSensors.filter(sensor => sensor.status === 'critical');
  const warningSensors = allSensors.filter(sensor => sensor.status === 'warning');

  return (
    <Box sx={{ p: 2 }}>
      {/* Заголовок та управління */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4">
            📡 {t('iot_dashboard_title') || 'IoT Моніторинг Обладнання'}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {t('iot_dashboard_subtitle') || 'Моніторинг датчиків обладнання в реальному часі'}
          </Typography>
        </Box>

        <Stack direction="row" spacing={2} alignItems="center">
          <Chip
            icon={getStatusIcon(connectionStatus)}
            label={t(`iot_status_${connectionStatus}`) || connectionStatus}
            color={getStatusColor(connectionStatus)}
            variant="outlined"
          />
          
          <Tooltip title={t('iot_settings_tooltip') || "Налаштування порогових значень"}>
            <IconButton onClick={() => setSettingsOpen(true)}>
              <Settings />
            </IconButton>
          </Tooltip>

          {!isMonitoring ? (
            <Button
              variant="contained"
              startIcon={<PlayArrow />}
              onClick={handleStartMonitoring}
            >
              {t('iot_start_monitoring') || 'Запустити моніторинг'}
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Stop />}
              onClick={handleStopMonitoring}
            >
              {t('iot_stop_monitoring') || 'Зупинити моніторинг'}
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Статистика */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('iot_total_machines') || 'Всього машин'}
              </Typography>
              <Typography variant="h4">
                {machines.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('iot_active_sensors') || 'Активні датчики'}
              </Typography>
              <Typography variant="h4">
                {allSensors.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('iot_critical_alerts') || 'Критичні сповіщення'}
              </Typography>
              <Typography variant="h4" color="error.main">
                {criticalSensors.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('iot_warning_alerts') || 'Попередження'}
              </Typography>
              <Typography variant="h4" color="warning.main">
                {warningSensors.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Сповіщення */}
      {criticalSensors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6">
            🚨 {t('iot_critical_alert_title') || 'Критичні аномалії виявлені!'}
          </Typography>
          <Typography>
            {t('iot_critical_alert_message') || 'Знайдено'} {criticalSensors.length} {t('iot_critical_sensors') || 'критичних датчиків'}
          </Typography>
        </Alert>
      )}

      {warningSensors.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography>
            ⚠️ {warningSensors.length} {t('iot_warning_sensors') || 'датчиків потребують уваги'}
          </Typography>
        </Alert>
      )}

      {/* Таблиця датчиків */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          {t('iot_sensors_table_title') || 'Дані датчиків в реальному часі'}
        </Typography>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('iot_machine_id') || 'Обладнання'}</TableCell>
                <TableCell>{t('iot_sensor_type') || 'Тип датчика'}</TableCell>
                <TableCell align="right">{t('iot_current_value') || 'Поточне значення'}</TableCell>
                <TableCell>{t('iot_status') || 'Статус'}</TableCell>
                <TableCell>{t('iot_last_update') || 'Останнє оновлення'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allSensors.map((sensor, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Typography fontWeight="medium">
                      {sensor.machineId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {getSensorIcon(sensor.sensorType)}
                      <Typography>
                        {t(`iot_sensor_${sensor.sensorType}`) || sensor.sensorType}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="h6" fontWeight="bold">
                      {sensor.value} {sensor.unit}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(sensor.status)}
                      label={t(`iot_sensor_status_${sensor.status}`) || sensor.status}
                      color={getStatusColor(sensor.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(sensor.timestamp).toLocaleTimeString()}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {allSensors.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {t('iot_no_data') || 'Немає даних з датчиків. Запустіть моніторинг.'}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Діалог налаштувань */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {t('iot_settings_title') || 'Налаштування порогових значень'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {Object.entries(thresholds).map(([sensorType, threshold]) => (
              <Box key={sensorType}>
                <Typography variant="h6" gutterBottom>
                  {t(`iot_sensor_${sensorType}`) || sensorType}
                </Typography>
                <Grid container spacing={2}>
                  {threshold.min !== undefined && (
                    <Grid item xs={6}>
                      <TextField
                        label={t('iot_min_threshold') || 'Мінімальне значення'}
                        type="number"
                        value={threshold.min}
                        onChange={(e) => setThresholds(prev => ({
                          ...prev,
                          [sensorType]: { ...prev[sensorType], min: parseFloat(e.target.value) }
                        }))}
                        fullWidth
                      />
                    </Grid>
                  )}
                  {threshold.max !== undefined && (
                    <Grid item xs={6}>
                      <TextField
                        label={t('iot_max_threshold') || 'Максимальне значення'}
                        type="number"
                        value={threshold.max}
                        onChange={(e) => setThresholds(prev => ({
                          ...prev,
                          [sensorType]: { ...prev[sensorType], max: parseFloat(e.target.value) }
                        }))}
                        fullWidth
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>
            {t('common_cancel') || 'Скасувати'}
          </Button>
          <Button onClick={handleUpdateThresholds} variant="contained">
            {t('common_save') || 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}